import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import contentEn from '../data/content_en.json';
import contentEs from '../data/content_es.json';

type Lang = 'en' | 'es';

interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}

const contentMap: Record<Lang, Record<string, string>> = {
  en: contentEn,
  es: contentEs,
};

function getInitialLang(): Lang {
  const stored = localStorage.getItem('portfolio-lang');
  if (stored === 'en' || stored === 'es') return stored;
  const browserLang = navigator.language.slice(0, 2);
  return browserLang === 'es' ? 'es' : 'en';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(getInitialLang);

  const toggleLang = useCallback(() => {
    setLang(prev => {
      const next = prev === 'en' ? 'es' : 'en';
      localStorage.setItem('portfolio-lang', next);
      return next;
    });
  }, []);

  const t = useCallback((key: string): string => {
    return contentMap[lang][key] ?? key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
}
